import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import TRAIcon from "../icons/TRAIcon";
import TRAButton from "../components/primitives/TRAButton";
import { useAuth } from "../../hooks/useAuth";
import {
  subscribeInbox,
  markNotificationRead,
  markAllNotificationsRead,
  formatUnreadBadgeCount,
  shouldHideNotificationBell,
  resolveNotificationHref,
  isNotificationUnread,
} from "../../services/notificationService";
import {
  NOTIFICATION_KIND_LABELS,
  NOTIFICATION_KINDS,
} from "../../domain/notifications/notificationTypes";
import { resolveFriendRequestFromNotification } from "../../features/lobby/utils/resolveFriendRequestFromNotification";
import { useFocusTrap } from "../utils/useFocusTrap";
import styles from "./shell.module.css";
import { cn } from "../utils/cn";

function formatWhen(ms) {
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function notificationHref(n) {
  return resolveNotificationHref(n);
}

/** Global notifications entry (header) — durable inbox when signed in. */
export default function NotificationsEntry({ className }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { uid, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState("");
  const [pulse, setPulse] = useState(false);
  const prevUnread = useRef(0);
  const wrapRef = useRef(null);
  const panelRef = useRef(null);

  const hide = shouldHideNotificationBell(pathname);
  const unread = items.filter(isNotificationUnread).length;
  const badge = formatUnreadBadgeCount(unread);

  const closePanel = () => setOpen(false);

  useFocusTrap({
    enabled: open,
    containerRef: panelRef,
    onEscape: closePanel,
  });

  useEffect(() => {
    if (hide || !isAuthenticated) {
      setItems([]);
      return undefined;
    }
    return subscribeInbox(uid, (rows) => setItems(rows || []));
  }, [uid, isAuthenticated, hide]);

  useEffect(() => {
    if (unread > prevUnread.current && unread > 0) {
      setPulse(true);
      const t = window.setTimeout(() => setPulse(false), 1200);
      prevUnread.current = unread;
      return () => window.clearTimeout(t);
    }
    prevUnread.current = unread;
    return undefined;
  }, [unread]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) closePanel();
    };
    document.addEventListener("mousedown", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
    };
  }, [open]);

  if (hide || !isAuthenticated) return null;

  const sorted = [...items].sort(
    (a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0)
  );

  const openNotification = async (n) => {
    await markNotificationRead(n.notificationId);
    setOpen(false);
    navigate(notificationHref(n));
  };

  const onFriendAction = async (action, n) => {
    const relationshipId = n?.meta?.relationshipId || n?.relationshipId;
    if (!relationshipId) return;
    setActionError("");
    setBusyId(relationshipId);
    try {
      const result = await resolveFriendRequestFromNotification(
        action,
        relationshipId
      );
      if (!result.ok) {
        setActionError(result.message || "Could not update request");
      }
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className={cn(styles.menuWrap, styles.notifyWrap, className)} ref={wrapRef}>
      <button
        type="button"
        className={cn(
          styles.iconBtn,
          styles.headerIconBtn,
          pulse && styles.notifyBellPulse
        )}
        aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? "tra-notifications-panel" : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <TRAIcon name="bell" size="md" />
        {badge && !open ? (
          <span className={styles.badgeCount} aria-hidden>
            {badge}
          </span>
        ) : null}
      </button>
      {open ? (
        <div
          id="tra-notifications-panel"
          ref={panelRef}
          className={styles.notifyPanel}
          role="dialog"
          aria-modal="true"
          aria-label="Notifications"
        >
          <div className={styles.notifyTitle}>
            <span className={styles.notifyEyebrow}>Notifications</span>
            <div className={styles.notifyTitleActions}>
              {unread > 0 ? (
                <button
                  type="button"
                  className={styles.notifyTextBtn}
                  onClick={() => markAllNotificationsRead(uid)}
                >
                  Mark all read
                </button>
              ) : null}
              <button
                type="button"
                className={cn(styles.iconBtn, styles.headerIconBtn)}
                aria-label="Close notifications"
                onClick={closePanel}
              >
                <TRAIcon name="close" size="sm" />
              </button>
            </div>
          </div>
          {actionError ? (
            <p className={styles.notifyError} role="alert">
              {actionError}
            </p>
          ) : null}
          {!sorted.length ? (
            <p className={styles.notifyEmpty}>No notifications yet.</p>
          ) : (
            <ul className={styles.notifyList}>
              {sorted.slice(0, 12).map((n) => {
                const kindLabel = NOTIFICATION_KIND_LABELS[n.kind] || "Update";
                const isFriendRequest = n.kind === NOTIFICATION_KINDS.FRIEND_REQUEST;
                const relationshipId = n.meta?.relationshipId || n.relationshipId;
                const friendName = n.meta?.fromPlayerName;
                const unreadRow = isNotificationUnread(n);
                return (
                  <li key={n.notificationId}>
                    <div
                      className={cn(
                        styles.notifyItem,
                        unreadRow && styles.notifyItemUnread
                      )}
                    >
                      <button
                        type="button"
                        className={styles.notifyItemMain}
                        onClick={() => openNotification(n)}
                      >
                        <span
                          className={cn(
                            styles.notifyItemTitle,
                            unreadRow && styles.notifyItemTitleUnread
                          )}
                        >
                          {n.title}
                          {n.groupCount > 1 ? ` (${n.groupCount})` : ""}
                        </span>
                        {friendName || n.body ? (
                          <span className={styles.notifyItemBody}>
                            {friendName || n.body}
                          </span>
                        ) : null}
                        <span className={styles.notifyItemMeta}>
                          {kindLabel}
                          {n.createdAtMs ? ` · ${formatWhen(n.createdAtMs)}` : ""}
                        </span>
                      </button>
                      {isFriendRequest && relationshipId ? (
                        <div className={styles.notifyActions}>
                          <TRAButton
                            type="button"
                            disabled={busyId === relationshipId}
                            loading={busyId === relationshipId}
                            onClick={() => onFriendAction("accept", n)}
                          >
                            Accept
                          </TRAButton>
                          <TRAButton
                            type="button"
                            variant="ghost"
                            disabled={busyId === relationshipId}
                            onClick={() => onFriendAction("decline", n)}
                          >
                            Decline
                          </TRAButton>
                        </div>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <div className={styles.notifyFooter}>
            <Link to="/activity" onClick={() => setOpen(false)}>
              View all activity
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
